import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const sharedData: Pick<LocDef, 'feature' | 'portal' | 'htmlAllowed' | 'shared'> = {
	feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
	portal: 'CP',
	htmlAllowed: false,
	shared: false,
};

const dpRoot = 'Profile|Affiliation';

export type CpProfileAffiliationFormLocs =
	| 'CPProfileAffiliationType'
	| 'CPProfileAffiliationTypeRequired'
	| 'CPProfileAffiliationTypeInvalid'
	| 'CPProfileAffiliationName'
	| 'CPProfileAffiliationNameRequired'
	| 'CPProfileAffiliationNameInvalid'
	| 'CPProfileAffiliationEmail'
	| 'CPProfileAffiliationEmailRequired'
	| 'CPProfileAffiliationEmailInvalid'
	| 'CPProfileAffiliationEmailUnsupported'
	| 'CPProfileAffiliationId'
	| 'CPProfileAffiliationIdRequired'
	| 'CPProfileAffiliationIdInvalid'
	| 'CPProfileAffiliationOtherName'
	| 'CPProfileAffiliationOtherNameInvalid'
	| 'CPProfileAffiliationOtherNameRequired'
	| 'CPProfileAffiliationUpdate'
	| 'CPProfileAffiliationSaveNotice';
export type CpProfileAffFormLocsType = LocMap<CpProfileAffiliationFormLocs>;

// The first localization includes multiple legacyName properties (commented) as they are being removed later.
export const cpSharedProfileAffiliationFormLocs: LocDefs<CpProfileAffiliationFormLocs> = {
	CPProfileAffiliationUpdate: {
		...sharedData,
		defaultValue: 'Update information',
		displayPath: `${dpRoot}|Update Information`,
		description: 'Update information button for the personal information section.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.Button.UpdateInfo',
		displayType: 'Button',
	},
	CPProfileAffiliationEmailInvalid: {
		...sharedData,
		defaultValue: 'Invalid email address',
		displayPath: `${dpRoot}|Invalid Email Address`,
		description: 'Error saying the email address is invalid.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationEmailInvalid',
		displayType: 'Error',
	},
	CPProfileAffiliationEmailRequired: {
		...sharedData,
		defaultValue: 'Affiliation email is required',
		displayPath: `${dpRoot}|Affiliation Email Required`,
		description: 'Error saying the affiliation email is required.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationEmailRequired',
		displayType: 'Error',
	},
	CPProfileAffiliationEmailUnsupported: {
		...sharedData,
		defaultValue: 'Unsupported email address',
		displayPath: `${dpRoot}|Affiliation Email Unsupported`,
		description: 'Error saying the affiliation email is not supported.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationEmailUnsupported',
		displayType: 'Error',
	},
	CPProfileAffiliationIdInvalid: {
		...sharedData,
		defaultValue: 'Affiliation ID is invalid',
		displayPath: `${dpRoot}|Affiliation ID Invalid`,
		description: 'Error saying the affiliation ID is invalid.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationIdInvalid',
		displayType: 'Error',
	},
	CPProfileAffiliationIdRequired: {
		...sharedData,
		defaultValue: 'Affiliation ID is required',
		displayPath: `${dpRoot}|Affiliation ID Required`,
		description: 'Error saying the affiliation ID is required.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationIdRequired',
		displayType: 'Error',
	},
	CPProfileAffiliationNameInvalid: {
		...sharedData,
		defaultValue: 'Affiliation name is invalid',
		displayPath: `${dpRoot}|Affiliation Name Invalid`,
		description: 'Error saying the affiliation name is invalid.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationNameInvalid',
		displayType: 'Error',
	},
	CPProfileAffiliationNameRequired: {
		...sharedData,
		defaultValue: 'Affiliation name is required',
		displayPath: `${dpRoot}|Afffiliation Name Required`,
		description: 'Error saying the affiliation name is required.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffiliationNameRequired',
		displayType: 'Error',
	},
	CPProfileAffiliationTypeInvalid: {
		...sharedData,
		defaultValue: 'Affiliation Type is invalid',
		displayPath: `${dpRoot}|Affiliation Type Invalid Error`,
		description: 'Error saying that affiliation Type is invalid.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffilliationTypeInvalid',
		displayType: 'Error',
	},
	CPProfileAffiliationTypeRequired: {
		...sharedData,
		defaultValue: 'Affiliation Type is required',
		displayPath: `${dpRoot}|Affiliation Type Required Error`,
		description: 'Error saying that affiliation Type is required.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.AffilliationTypeRequired',
		displayType: 'Error',
	},
	CPProfileAffiliationOtherNameInvalid: {
		...sharedData,
		defaultValue: 'Other affiliation name is invalid',
		displayPath: `${dpRoot}|Other Affiliation Name Invalid Error`,
		description: 'Error saying that the other affiliation name is invalid.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.OtherNameInvalid',
		displayType: 'Error',
	},
	CPProfileAffiliationOtherNameRequired: {
		...sharedData,
		defaultValue: 'Other affiliation name is required',
		displayPath: `${dpRoot}|Other Affiliation Name Required Error`,
		description: 'Error saying that the other affiliation name is required.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldError.OtherNameRequired',
		displayType: 'Error',
	},
	CPProfileAffiliationEmail: {
		...sharedData,
		defaultValue: 'Affiliation Email',
		displayPath: `${dpRoot}|Affiliation Email`,
		description: 'Field label for: Affiliation Email.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldLabel.AffiliationEmail',
		displayType: 'Label',
	},
	CPProfileAffiliationId: {
		...sharedData,
		defaultValue: 'Affiliation ID',
		displayPath: `${dpRoot}|Affiliation ID`,
		description: 'Field label for: Affiliation ID.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldLabel.AffiliationId',
		displayType: 'Label',
	},
	CPProfileAffiliationName: {
		...sharedData,
		defaultValue: 'Affiliation Name',
		displayPath: `${dpRoot}|Affiliation Name`,
		description: 'Label for the affiliation name input.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldLabel.AffiliationName',
		displayType: 'Label',
	},
	CPProfileAffiliationType: {
		...sharedData,
		defaultValue: 'Affiliation Type',
		displayPath: `${dpRoot}||Affiliation Type`,
		description: 'Label for the affiliation type input.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldLabel.AffiliationType',
		displayType: 'Label',
	},
	CPProfileAffiliationOtherName: {
		...sharedData,
		defaultValue: 'Other Affiliation Name',
		displayPath: `${dpRoot}|Other Affiliation Name Label`,
		description: 'The field label for the other affiliation name.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.FieldLabel.OtherName',
		displayType: 'Label',
	},
	CPProfileAffiliationSaveNotice: {
		...sharedData,
		defaultValue: 'Changes saved',
		displayPath: `${dpRoot}|Changes Saved`,
		description: 'Notification message when affiliation changes have been successfully saved.',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.NotificationMessage.ChangesSaved',
		displayType: 'Content',
	},
};

export const CpProfileAffFormLocs = MapLocKeys<CpProfileAffiliationFormLocs>(
	cpSharedProfileAffiliationFormLocs
);
